/**
 * @Module: Firestore Error Types
 * @Status: Ready
 * @Summary: Typed error hierarchy for Firestore operations — never swallow
 * @Description: AppError wraps every Firestore failure with a machine-readable
 *   code and human-readable context. wrapFirestoreError translates Firebase SDK
 *   error codes into AppError so callers never touch raw Firebase errors.
 * @NextSteps: Add retry-eligible flag per code for automatic retry logic
 */

export type ErrorCode =
  | 'AUTH_REQUIRED'
  | 'INVALID_INPUT'
  | 'NOT_FOUND'
  | 'NETWORK_ERROR'
  | 'PERMISSION_DENIED'
  | 'SERVICE_UNAVAILABLE'
  | 'SIZE_LIMIT'
  | 'UNKNOWN';

export class AppError extends Error {
  code: ErrorCode;
  context?: string;

  constructor(code: ErrorCode, message: string, context?: string) {
    super(message);
    this.code = code;
    this.context = context;
    this.name = 'AppError';
  }
}

/**
 * Convert a raw Firebase/Firestore error into an AppError.
 * Always throws — never returns. Callers use this in catch blocks.
 */
export function wrapFirestoreError(error: unknown, context: string): never {
  if (error instanceof AppError) throw error;

  const firebaseCode =
    error instanceof Error && 'code' in error
      ? (error as { code: string }).code
      : '';

  const message =
    error instanceof Error ? error.message : 'Unknown Firestore error';

  // Detect Firestore not provisioned / API not enabled in the Firebase project
  const isServiceUnavailable =
    message.includes('Cloud Firestore API has not been used') ||
    message.includes('NOT_FOUND') ||
    message.includes('Failed to get document because the client is offline') ||
    firebaseCode === 'failed-precondition';

  if (isServiceUnavailable) {
    throw new AppError(
      'SERVICE_UNAVAILABLE',
      'Firestore is not available — enable it in the Firebase console for cloud sync.',
      context,
    );
  }

  switch (firebaseCode) {
    case 'permission-denied':
      throw new AppError('PERMISSION_DENIED', message, context);
    case 'not-found':
      throw new AppError('NOT_FOUND', message, context);
    case 'unavailable':
    case 'deadline-exceeded':
      throw new AppError('NETWORK_ERROR', message, context);
    case 'resource-exhausted':
      throw new AppError('SIZE_LIMIT', message, context);
    case 'unauthenticated':
      throw new AppError('AUTH_REQUIRED', message, context);
    default:
      throw new AppError('UNKNOWN', message, context);
  }
}
